import omit from 'lodash/omit';
import pick from 'lodash/pick';
import type {
  ArdoqId,
  FutureState,
  FutureStatePreview,
  Initiative,
  RenderResponse,
} from '@ardoq/api-types';
import type { ArdoqError } from '@ardoq/common-helpers';
import type { FutureStateProposalItem } from './FutureStatePage';

export const isRealized = (
  evaluations: RenderResponse['evaluations'],
  claimIds: ArdoqId[]
): boolean =>
  claimIds.length > 0 &&
  claimIds.every(claimId => evaluations[claimId]?.status === 'satisfied');

export const isProposalSelected = (
  item: FutureStateProposalItem,
  selectedNodeId: string | null
): boolean =>
  selectedNodeId !== null &&
  (item.id === selectedNodeId || item.focusNodeId === selectedNodeId);

const groupHoldsSelection = (
  groupItems: FutureStateProposalItem[],
  selectedNodeId: string | null
): boolean => groupItems.some(item => isProposalSelected(item, selectedNodeId));

export const isGroupExpanded = (
  groupId: string,
  groupItems: FutureStateProposalItem[],
  selectedNodeId: string | null,
  collapsedGroupIds: Set<string>
): boolean =>
  groupHoldsSelection(groupItems, selectedNodeId) ||
  !collapsedGroupIds.has(groupId);

export const shouldDeselectOnCollapse = (
  groupId: string,
  isExpanded: boolean,
  items: FutureStateProposalItem[],
  selectedNodeId: string | null
): boolean =>
  isExpanded &&
  groupHoldsSelection(
    items.filter(item => item.groupId === groupId),
    selectedNodeId
  );

/**
 * A FutureState is approved when its most recent approval action is `approve`.
 */
export const isFutureStateApproved = (
  futureState: Pick<FutureStatePreview, 'approval'>
): boolean => futureState.approval?.action === 'approve';

export type FetchStatus = 'idle' | 'loading' | 'error';

export type FutureStateStoreState = {
  entries: Record<ArdoqId, Record<ArdoqId, FutureState>>;
  status: Record<ArdoqId, Record<ArdoqId, FetchStatus>>;
  errors: Record<ArdoqId, Record<ArdoqId, ArdoqError>>;
};

export const defaultFutureStateStoreState: FutureStateStoreState = {
  entries: {},
  status: {},
  errors: {},
};

const omitKey = <V>(
  source: Record<ArdoqId, V> | undefined,
  key: ArdoqId
): Record<ArdoqId, V> => (source ? omit(source, key) : {});

export const upsertFutureState = (
  state: FutureStateStoreState,
  {
    initiativeId,
    futureState,
  }: { initiativeId: ArdoqId; futureState: FutureState }
): FutureStateStoreState => ({
  entries: {
    ...state.entries,
    [initiativeId]: {
      ...state.entries[initiativeId],
      [futureState._id]: futureState,
    },
  },
  status: {
    ...state.status,
    [initiativeId]: {
      ...state.status[initiativeId],
      [futureState._id]: 'idle',
    },
  },
  errors: {
    ...state.errors,
    [initiativeId]: omitKey(state.errors[initiativeId], futureState._id),
  },
});

export const markLoading = (
  state: FutureStateStoreState,
  {
    initiativeId,
    futureStateId,
  }: { initiativeId: ArdoqId; futureStateId: ArdoqId }
): FutureStateStoreState => ({
  ...state,
  status: {
    ...state.status,
    [initiativeId]: {
      ...state.status[initiativeId],
      [futureStateId]: 'loading',
    },
  },
  errors: {
    ...state.errors,
    [initiativeId]: omitKey(state.errors[initiativeId], futureStateId),
  },
});

export const markError = (
  state: FutureStateStoreState,
  {
    initiativeId,
    futureStateId,
    error,
  }: { initiativeId: ArdoqId; futureStateId: ArdoqId; error: ArdoqError }
): FutureStateStoreState => ({
  ...state,
  status: {
    ...state.status,
    [initiativeId]: {
      ...state.status[initiativeId],
      [futureStateId]: 'error',
    },
  },
  errors: {
    ...state.errors,
    [initiativeId]: {
      ...state.errors[initiativeId],
      [futureStateId]: error,
    },
  },
});

export const evictInitiative = (
  state: FutureStateStoreState,
  { initiativeId }: { initiativeId: ArdoqId }
): FutureStateStoreState => ({
  entries: omitKey(state.entries, initiativeId),
  status: omitKey(state.status, initiativeId),
  errors: omitKey(state.errors, initiativeId),
});

// Reconcile cached full FutureState payloads against the refreshed Initiative
// preview list. Future state ids no longer in the preview list are evicted —
// they have been deleted or moved out of this initiative. Surviving ids are
// left untouched: preview-projection field drift (e.g. renamed `name`,
// changed `approval`) is the BE's responsibility to surface via a separate
// `futureStateUpdated` event, not for the FE to synthesize from previews.
export const reconcileWithInitiative = (
  state: FutureStateStoreState,
  initiative: Initiative
): FutureStateStoreState => {
  const initiativeId = initiative._id;
  const liveIds = initiative.futureStates.map(preview => preview._id);
  const currentEntries = state.entries[initiativeId];
  const currentStatus = state.status[initiativeId];
  const currentErrors = state.errors[initiativeId];
  if (!currentEntries && !currentStatus && !currentErrors) return state;
  return {
    ...state,
    entries: currentEntries
      ? { ...state.entries, [initiativeId]: pick(currentEntries, liveIds) }
      : state.entries,
    status: currentStatus
      ? { ...state.status, [initiativeId]: pick(currentStatus, liveIds) }
      : state.status,
    errors: currentErrors
      ? { ...state.errors, [initiativeId]: pick(currentErrors, liveIds) }
      : state.errors,
  };
};

export const getEntry = (
  state: FutureStateStoreState,
  {
    initiativeId,
    futureStateId,
  }: { initiativeId: ArdoqId; futureStateId: ArdoqId }
): FutureState | undefined => state.entries[initiativeId]?.[futureStateId];

export const getStatus = (
  state: FutureStateStoreState,
  {
    initiativeId,
    futureStateId,
  }: { initiativeId: ArdoqId; futureStateId: ArdoqId }
): FetchStatus => state.status[initiativeId]?.[futureStateId] ?? 'idle';

export const getError = (
  state: FutureStateStoreState,
  {
    initiativeId,
    futureStateId,
  }: { initiativeId: ArdoqId; futureStateId: ArdoqId }
): ArdoqError | undefined => state.errors[initiativeId]?.[futureStateId];
